import { observer } from 'mobx-react-lite';

import * as d3 from 'd3';
import _ from 'lodash';
import { makeAutoObservable } from 'mobx';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMobxInstance } from '../../utils/mobxUtil';
import { ChevronRightIcon, CircleArrowRight } from 'lucide-react';

// 스켈레톤용 샘플 width 값들
const SKELETON_SAMPLE_WIDTHS = ['100%', '70%', '50%', '30%', '20%'];

class ViewStore<Data = unknown> {
    graphItems: ChangeBarGraphItem<Data>[] = [];
    changePercentScale: d3.ScaleLinear<number, number> | null = null;
    keepItemOrder: boolean = false;

    constructor(keepItemOrder: boolean) {
        this.keepItemOrder = keepItemOrder;
        makeAutoObservable(this);
    }

    computeItems(items: ChangeBarGraphItem<Data>[]) {
        // changePercent 순으로 정렬
        this.graphItems = this.keepItemOrder
            ? items
            : _.sortBy(items, (item) => -item.changePercent);

        // 스케일 계산
        const allChangePercents = this.graphItems.map((item) => Math.abs(item.changePercent));
        const maxAbsChangePercent = d3.max(allChangePercents) || 0;
        this.changePercentScale = d3.scaleLinear().domain([0, maxAbsChangePercent]).range([0, 100]);
    }
}

export type ChangeBarGraphItem<Data = unknown> = {
    name: string;
    subName?: string;
    changePercent: number;
    isBenchmark?: boolean;
    /**
     * 추가로 전달할 데이터
     */
    data?: Data;
};

const ChangeBarGraph = observer(
    <Data extends unknown = unknown>({
        items,
        highligthPercent = 1,
        className,
        onClickBar,
        nameMinWidth = 92,
        isShowArrow = false,
        keepItemOrder = false,
        isShowSkeleton = false,
        skeletonCount = 5,
    }: {
        items: ChangeBarGraphItem<Data>[];
        /**
         * 몇 % 이상일 때 강조할 것인지
         */
        highligthPercent?: number;
        className?: string;
        nameMinWidth?: number;
        onClickBar?: (item: ChangeBarGraphItem<Data>) => void;
        /**
         * 클릭 가능 여부를 표시하는 화살표를 보여줄지 여부
         * - 벤치마크에는 보여주지 않음
         */
        isShowArrow?: boolean | ((item: ChangeBarGraphItem<Data>) => boolean);
        /**
         * 아이템 순서를 유지할지 여부
         * - 기본값은 false
         * - true 일 때는 items 의 순서를 유지함
         * - false 일 때는 changePercent 순으로 정렬함
         */
        keepItemOrder?: boolean;
        /**
         * 스켈레톤을 보여줄지 여부
         */
        isShowSkeleton?: boolean;
        /**
         * 스켈레톤 개수
         */
        skeletonCount?: number;
    }) => {
        const viewStore = useMobxInstance(ViewStore<Data>, keepItemOrder);

        useEffect(() => {
            viewStore.computeItems(items);
        }, [viewStore, items]);

        return (
            <div className={twMerge('flex flex-col gap-2', className)}>
                {isShowSkeleton &&
                    _.isEmpty(viewStore.graphItems) &&
                    _.times(skeletonCount, (index) => (
                        <div key={`skeleton-${index}`} className="px-5">
                            <div className="flex items-center gap-2">
                                <div className="shrink-0" style={{ minWidth: `${nameMinWidth}px` }}>
                                    <div className="skeleton h-7.5 w-full rounded-sm"></div>
                                </div>
                                <div className="relative flex h-7.5 w-full items-center">
                                    <div
                                        className="skeleton h-full rounded-xs"
                                        style={{
                                            width: SKELETON_SAMPLE_WIDTHS[
                                                index % SKELETON_SAMPLE_WIDTHS.length
                                            ],
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    ))}

                {_.map(viewStore.graphItems, (item) => {
                    const changePercent = item.changePercent;
                    const absChangePercent = Math.abs(changePercent);
                    const isPositive = changePercent >= 0;
                    const shouldHighlight = absChangePercent >= highligthPercent;
                    const isShowBarArrow =
                        typeof isShowArrow === 'function' ? isShowArrow(item) : isShowArrow;

                    return (
                        <div
                            key={item.name}
                            className={twMerge(
                                'px-5',
                                item.isBenchmark &&
                                    'bg-base-200 border-base-300 my-1 border-y py-2 font-semibold',
                            )}
                            role={onClickBar ? 'button' : undefined}
                            onClick={(e) => {
                                e.preventDefault();
                                onClickBar?.(item);
                            }}
                        >
                            <div className="flex items-center gap-2">
                                <div
                                    className={twMerge(
                                        'text-base-content/70 shrink-0',
                                        shouldHighlight && 'text-base-content font-bold',
                                        item.isBenchmark && 'text-base-content',
                                    )}
                                    style={{ minWidth: `${nameMinWidth}px` }}
                                >
                                    {item.name}
                                    {item.subName && (
                                        <div className="text-base-content/40 -mt-1 text-xs font-normal">
                                            {item.subName}
                                        </div>
                                    )}
                                </div>
                                <div className="relative flex h-7.5 w-full items-center">
                                    {/* 막대 차트 배경 */}
                                    <div
                                        className={twMerge(
                                            'absolute top-0 left-0 h-full rounded-xs',
                                            isPositive ? 'bg-up/30' : 'bg-down/30',
                                        )}
                                        style={{
                                            width: `${viewStore.changePercentScale?.(absChangePercent) || 0}%`,
                                        }}
                                    />
                                    {/* 변화율 텍스트 */}
                                    <div className="absolute top-0 left-0 z-10 flex h-full items-center px-2">
                                        <div
                                            className={twMerge(
                                                'text-sm tabular-nums',
                                                shouldHighlight && 'font-bold',
                                            )}
                                        >
                                            {changePercent > 0 ? '+' : ''}
                                            {changePercent.toFixed(2)}%
                                        </div>
                                    </div>
                                    {isShowBarArrow && (
                                        <div className="absolute top-0 right-0 flex h-full items-center justify-center">
                                            <ChevronRightIcon className="text-base-content/15 size-5" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    },
);

export default ChangeBarGraph;
